import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Popover from "@radix-ui/react-popover";
import { Search } from "lucide-react";
import { useProjects } from "../lib/projects";

export function TopSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const { data } = useProjects();
  const allProjects = useMemo(() => data?.items ?? [], [data]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allProjects.slice(0, 8);
    return allProjects
      .filter((p) =>
        `${p.name} ${p.subdomain ?? ""} ${p.customHostname ?? ""}`
          .toLowerCase()
          .includes(q),
      )
      .slice(0, 8);
  }, [allProjects, query]);

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  // "/"-Shortcut focuses input
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable)
      ) {
        return;
      }
      if (e.key === "/") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const select = (id: number) => {
    setOpen(false);
    setQuery("");
    inputRef.current?.blur();
    navigate(`/projects/${id}/overview`);
  };

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(matches.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      const m = matches[highlight];
      if (m) {
        e.preventDefault();
        select(m.id);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <Popover.Root open={open && matches.length > 0} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <div className="relative w-full max-w-md">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
          />
          <input
            ref={inputRef}
            type="search"
            placeholder="Projekt suchen…"
            aria-label="Projekt schnell suchen"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onInputKey}
            className="h-8 w-full rounded-md border border-border-primary bg-bg-tertiary pl-8 pr-12 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none"
          />
          {query.length === 0 && (
            <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-border-primary bg-bg-secondary px-1.5 py-0.5 font-mono text-[10px] text-text-tertiary">
              /
            </kbd>
          )}
        </div>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="z-dropdown w-[var(--radix-popover-trigger-width)] max-w-md rounded-md border border-border-primary bg-bg-elevated p-1 shadow-md animate-fade-in"
        >
          <ul role="listbox">
            {matches.map((p, i) => (
              <li
                key={p.id}
                role="option"
                aria-selected={i === highlight}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => select(p.id)}
                className={`flex cursor-pointer items-baseline justify-between rounded-sm px-2 py-1.5 text-sm ${
                  i === highlight
                    ? "bg-bg-selected text-text-primary"
                    : "text-text-primary hover:bg-bg-hover"
                }`}
              >
                <span className="font-medium">{p.name}</span>
                <span className="ml-3 truncate font-mono text-xs text-text-tertiary">
                  {p.subdomain ? `${p.subdomain}.dhc-track.io` : "—"}
                </span>
              </li>
            ))}
          </ul>
          {matches.length === 0 && query && (
            <p className="px-2 py-2 text-xs text-text-tertiary">
              Kein Treffer
            </p>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
